<template>
  <form @submit.prevent="handleSubmit">
    <div v-if="isLoading" class="loading-state">
      <i class="pi pi-spin pi-spinner"></i>
      {{ t('Carregando...') }}
    </div>

    <div v-else>
      <!-- Current Stock Info -->
      <div v-if="currentStock" class="current-stock-info">
        <p>
          <strong>{{ t('Produto') }}:</strong> {{ currentStock.so_product_name }}
        </p>
        <p>
          <strong>{{ t('Franquia') }}:</strong>
          <span data-testid="stock-adjust-franchise-info">{{ franchiseName }}</span>
        </p>
        <p>
          <strong>{{ t('Estoque Atual') }}:</strong> {{ currentStock.so_stock_quantity }}
        </p>
        <p>
          <strong>{{ t('Preço Atual') }}:</strong>
          {{ n(effectivePrice, 'currency') }}
          <small data-testid="stock-adjust-effective-price-info" v-if="currentStock.so_stock_price != null">
            ({{ t('preço específico desta franquia') }})
          </small>
          <small data-testid="stock-adjust-effective-price-info" v-else>
            ({{ t('catálogo') }})
          </small>
        </p>
      </div>

      <div class="form-field">
        <input-number
          elem-id="stockQuantity"
          v-model="formData.quantity"
          :label="t('Nova Quantidade')"
          :placeholder="t('Digite a nova quantidade')"
          :min="0"
          :showButtons="true"
          :step="1"
          ng-required
          :validation="validateQuantity"
          fluid
          data-testid="so-stock-adjust-quantity-input"
        />
        <small v-if="errors.quantity" class="error-message">{{ errors.quantity }}</small>
      </div>

      <div class="form-field">
        <input-currency
          elem-id="stockFranchisePrice"
          v-model="formData.so_stock_price"
          :label="t('Preço para esta franquia')"
          :placeholder="t('R$ 0,00')"
          fluid
          data-testid="stock-adjust-price"
        />
        <small>{{ t('opcional') }} — {{ t('deixe em branco para manter o preço atual') }}</small>
      </div>

      <div class="form-field">
        <input-number
          elem-id="stockMinimumQuantity"
          v-model="formData.so_stock_minimum_quantity"
          :label="t('Alerta estoque baixo')"
          :placeholder="t('0')"
          :min="0"
          :showButtons="true"
          :step="1"
          fluid
          data-testid="stock-adjust-minimum-quantity"
        />
        <small>{{ t('Quando o estoque atingir ou ficar abaixo deste valor, o produto será destacado em âmbar na lista e contará no alerta de "Estoque baixo" no topo da página. Deixe em 0 ou em branco para desativar.') }}</small>
      </div>

      <div class="form-field">
        <label for="stockNotes" class="textarea-label">{{ t('Observações') }}</label>
        <input-text
          elem-id="stockNotes"
          v-model="formData.notes"
          :placeholder="t('Motivo do ajuste (opcional)')"
          :multiline="true"
          :rows="3"
          fluid
          data-testid="so-stock-adjust-notes-input"
        />
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOStock } from '../../composables/useSOStock';
import { permissionsService } from '../../services/permissionsService';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface CurrentStock {
  so_product_name?: string;
  so_stock_quantity?: number | string | null;
  so_stock_minimum_quantity?: number | string | null;
  so_product_default_price?: number;
  so_stock_price?: number;
  [key: string]: unknown;
}

interface AdjustError {
  message?: string;
  stack?: string;
  response?: { data?: { error?: string } };
}

interface Props {
  productId?: Id | null;
  franchiseId?: Id | null;
}

const props = withDefaults(defineProps<Props>(), {
  productId: null,
  franchiseId: null
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

// Get i18n composable
const { t, n } = useI18n();

// Use composables
const { getStock, adjustStock } = useSOStock();

// Convert productId to number for consistency
const productIdNumber = computed<number | null>(() => {
  if (!props.productId) return null;
  const num = typeof props.productId === 'string' ? parseInt(props.productId, 10) : props.productId;
  log('SOStockAdjustForm', 'productIdNumber', 'Converted ID', { original: props.productId, converted: num });
  return num;
});

// Reactive data
const formData = reactive<{ quantity: number | undefined; so_stock_price: number | null; so_stock_minimum_quantity: number | null; notes: string }>({
  quantity: undefined,
  so_stock_price: null,
  so_stock_minimum_quantity: null,
  notes: ''
});

const errors = reactive<{ quantity: string }>({
  quantity: ''
});

const isLoading = ref<boolean>(false);
const currentStock = ref<CurrentStock | null>(null);

const effectivePrice = computed<number>(() => {
  return currentStock.value?.so_stock_price ?? currentStock.value?.so_product_default_price ?? 0;
});

const franchiseName = computed<string>(() => {
  // Prefer actual franchise name from loaded stock data
  if (currentStock.value?.so_stock_franchise_name) {
    return String(currentStock.value.so_stock_franchise_name);
  }
  // Fallback to service lookup by ID
  if (!props.franchiseId) return '';
  const name = permissionsService.getFranchiseName(Number(props.franchiseId));
  return name || String(props.franchiseId);
});

// Validation function for InputNumber component
const validateQuantity = (): boolean => {
  return formData.quantity !== null && formData.quantity !== undefined && formData.quantity >= 0;
};

const loadCurrentStock = async (): Promise<void> => {
  if (!productIdNumber.value) {
    logWarn('SOStockAdjustForm', 'loadCurrentStock', 'No productId provided');
    return;
  }

  log('SOStockAdjustForm', 'loadCurrentStock', 'Loading current stock', productIdNumber.value);
  isLoading.value = true;

  try {
    const franchiseId = props.franchiseId ? Number(props.franchiseId) : undefined;
    const stock = await getStock(productIdNumber.value, franchiseId) as CurrentStock | null | undefined;
    log('SOStockAdjustForm', 'loadCurrentStock', 'Loaded stock data', stock);

    if (!stock) {
      throw new Error('Dados do estoque nao encontrados');
    }

    currentStock.value = stock;
    formData.quantity = stock.so_stock_quantity != null ? Number(stock.so_stock_quantity) : 0;
    formData.so_stock_price = stock.so_stock_price != null ? Number(stock.so_stock_price) : null;
    formData.so_stock_minimum_quantity = stock.so_stock_minimum_quantity != null ? Number(stock.so_stock_minimum_quantity) : null;
    clearErrors();
  } catch (error: unknown) {
    const err = error as AdjustError;
    logError('SOStockAdjustForm', 'loadCurrentStock', 'Error loading stock', {
      productId: productIdNumber.value,
      error: err.message,
      stack: err.stack
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar estoque') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOStockAdjustForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (formData.quantity === null || formData.quantity === undefined) {
    errors.quantity = t('Campo obrigatório');
    isValid = false;
  } else if (formData.quantity < 0) {
    errors.quantity = t('Quantidade não pode ser negativa');
    isValid = false;
  }

  log('SOStockAdjustForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.quantity = '';
};

const resetForm = (): void => {
  log('SOStockAdjustForm', 'resetForm', 'Resetting form');
  formData.quantity = undefined;
  formData.so_stock_price = null;
  formData.so_stock_minimum_quantity = null;
  formData.notes = '';
  currentStock.value = null;
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  log('SOStockAdjustForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOStockAdjustForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const payload: {
      quantity: number;
      notes?: string;
      so_stock_price?: number;
      so_stock_minimum_quantity?: number;
    } = {
      quantity: formData.quantity!,
      notes: formData.notes ? formData.notes.trim() : undefined,
    };
    if (formData.so_stock_price !== null && formData.so_stock_price !== undefined) {
      payload.so_stock_price = Math.round(Number(formData.so_stock_price) * 100);
    }
    if (formData.so_stock_minimum_quantity !== null && formData.so_stock_minimum_quantity !== undefined) {
      payload.so_stock_minimum_quantity = Math.max(0, Math.round(Number(formData.so_stock_minimum_quantity)));
    }

    log('SOStockAdjustForm', 'handleSubmit', 'Adjusting stock', {
      productId: productIdNumber.value,
      payload
    });

    if (productIdNumber.value === null) {
      throw new Error('productId is required');
    }

    if (!props.franchiseId) {
      throw new Error(t('Franquia não informada'));
    }

    const adjFranchiseId = Number(props.franchiseId);
    await adjustStock(productIdNumber.value, adjFranchiseId, payload);

    eventBus.emit('globalMessage.success', t('Estoque ajustado com sucesso'));
    emit('submit-success');
    resetForm();
  } catch (error: unknown) {
    logError('SOStockAdjustForm', 'handleSubmit', 'Error adjusting stock', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao ajustar estoque') + ': ' + message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in productId to reload data
watch(() => props.productId, async (newId) => {
  log('SOStockAdjustForm', 'watch', 'productId changed', { newId });
  if (newId) {
    await nextTick();
    await loadCurrentStock();
  }
});

// Lifecycle
onMounted(() => {
  log('SOStockAdjustForm', 'mounted', 'Component mounted', {
    productId: props.productId,
    productIdNumber: productIdNumber.value
  });

  if (productIdNumber.value) {
    loadCurrentStock();
  }
});

// Expose methods for parent components
defineExpose({
  loadCurrentStock,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.current-stock-info {
  background-color: var(--p-surface-100);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.current-stock-info p {
  margin: 0.25rem 0;
  font-size: 0.95rem;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: var(--p-text-muted-color);
}

.textarea-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
}
</style>
